// global imports
import { CircularProgress, Input, makeStyles, Theme } from '@material-ui/core';
import { StyleRules } from '@material-ui/core/styles';
import cx from 'classnames';
import React, { useEffect, useState } from 'react';
import 'react-datepicker/dist/react-datepicker.css';
import { withLocalize } from 'react-localize-redux';
import { connect } from 'react-redux';

// project imports
import Button from '../../../../components/CustomButtons/Button.jsx';
import * as authActions from '../../../../store/actions/auth_actions';
import * as shopifyActions from '../../../../store/actions/shopify_actions';

// project types imports
import { IState } from '../../../../store/reducers';
import { IStyleProps, PropsClasses } from '../../../../types/StyleProps';

// local imports
import commonStyles from '../../../../assets/jss/commonStyles';
import { DispatchProps, OwnProps, Props, StateProps } from './types';

const useStyles = makeStyles<Theme, IStyleProps>(() => commonStyles as StyleRules);

const ImportOrder: React.FC<Props & typeof defaultProps> = props => {
  const classes: PropsClasses = useStyles({} as IStyleProps);

  const {
    translate,
    OAuthURL,
    testConnection,
    testConnectionTimeout,
    connectionStatus,
    fetchOAuthUrlRequest,
    importOrderRequest,
    testConnectionForShopRequest,
    shopifyShops,
  } = props;

  const [testDelayActive, setTestDelayActive] = useState<any>(false);

  const [orderID, setOrderID] = useState<string>();

  // didMount fetch data
  useEffect(() => {
    if (testConnection) {
      if (testConnectionTimeout) {
        setTestDelayActive(true);
      }
      setTimeout(() => {
        const shop = shopifyShops.find(s => s.active);
        if (shop) {
          testConnectionForShopRequest({ shop });
        }
        if (!OAuthURL) {
          fetchOAuthUrlRequest({ system: 'shopify' });
        }
        setTestDelayActive(false);
      }, testConnectionTimeout);
    }
  }, []);

  // test connection status
  useEffect(() => {
    if (connectionStatus) {
      if (connectionStatus === 'nok' || connectionStatus === 'nook') {
        fetchOAuthUrlRequest({ system: 'shopify' });
      }
    }
  }, [connectionStatus]);

  const importOrder = () => {
    if (!orderID) {
      return;
    }
    const shop = shopifyShops.find(s => s.active);
    if (shop) {
      importOrderRequest({ shop: shop.name, name: orderID.replace(/#/g, '%23') });
    }
  };

  return (
    <>
      {testDelayActive || connectionStatus !== 'loading'
        ? connectionStatus === 'ok' && (
            <>
              <p className={classes.bodyLg}>{translate('import_order_by_id')}</p>
              <div
                className={cx(classes.runLabel, {
                  [classes.flexCenter]: !props.shopifyLastRun || props.shopifyLastRun.exitState !== 'error',
                })}
              >
                <div
                  className={cx({
                    [classes.selectSmall]: true,
                    [classes.contentContainer]: true,
                    [classes.flexCenter]: true,
                    [classes.widthAuto]: true,
                  })}
                >
                  <label className={classes.datepickerLabel}>{translate('order_import_name')}</label>
                  <Input type="text" onChange={(evt: any) => setOrderID(evt.target.value)} />
                </div>
                <Button className={classes.formButton} type="button" color="primary" onClick={importOrder}>
                  {translate('run.run')}
                </Button>
              </div>
              {props.shopifyImportOrderStatus === 'loading' && <CircularProgress />}
              {props.shopifyImportOrderStatus === 'ok' && (
                <p className={classes.iconGreen}>
                  {translate('successfully_imported', undefined, { renderInnerHtml: true })}
                </p>
              )}
              {props.shopifyImportOrderStatus === 'failed' && (
                <p className={classes.iconRed}>{translate('unsuccessfully_imported')}</p>
              )}
            </>
          )
        : props.showSpinner && <CircularProgress />}
    </>
  );
};

const defaultProps: OwnProps = {
  testConnection: false,
  testConnectionTimeout: 3000,
  showSpinner: false,
};
ImportOrder.defaultProps = defaultProps;

const mapStateToProps = (state: IState): StateProps => ({
  OAuthURL: state.shopify.OAuthURL,
  shopifyLastRun: state.shopify.lastRun,
  shopifyLastJob: state.shopify.lastJob,
  connectionStatus: state.shopify.connectionStatus,
  shopifyImportOrderStatus: state.shopify.importOrderStatus,
  shopifyShops: state.shopify.shops,
});

const mapDispatchToProps = {
  fetchOAuthUrlRequest: authActions.fetchOAuthUrlRequest,
  importOrderRequest: shopifyActions.importOrderRequest,
  testConnectionForShopRequest: shopifyActions.testConnectionForShopRequest,
};

export default connect<StateProps, DispatchProps, OwnProps, any>(
  mapStateToProps,
  mapDispatchToProps,
)(withLocalize(ImportOrder));
